import { AccessType } from "@mirage/shared/compose/assist-api";
import type { AssistTemplate } from "@mirage/shared/compose/assist-template";
import { useMemo } from "react";

export const TEMPLATE_FILTERS = {
  YOUR: "your",
  COMPANY: "company",
  TYPES: "types",
} as const;

export type TemplateFilterType =
  (typeof TEMPLATE_FILTERS)[keyof typeof TEMPLATE_FILTERS];

interface UseTemplateFilteringProps {
  templates: AssistTemplate[];
  searchQuery: string;
  selectedFilters: TemplateFilterType[];
}

export function useTemplateFiltering({
  templates,
  searchQuery,
  selectedFilters,
}: UseTemplateFilteringProps) {
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    if (searchQuery) {
      filtered = filtered.filter((template) =>
        template.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    const hasFilters = selectedFilters.length > 0;
    const hasOwnershipFilters =
      selectedFilters.includes(TEMPLATE_FILTERS.YOUR) ||
      selectedFilters.includes(TEMPLATE_FILTERS.COMPANY);

    if (hasFilters && !hasOwnershipFilters) {
      return filtered;
    }

    if (hasFilters && hasOwnershipFilters) {
      const hasYourFilter = selectedFilters.includes(TEMPLATE_FILTERS.YOUR);
      const hasCompanyFilter = selectedFilters.includes(
        TEMPLATE_FILTERS.COMPANY,
      );

      filtered = filtered.filter((template) => {
        if (!("accessType" in template)) {
          return false;
        }
        return (
          (hasYourFilter && template.accessType === AccessType.INDIVIDUAL) ||
          (hasCompanyFilter && template.accessType === AccessType.TEAM)
        );
      });
    }

    return filtered;
  }, [templates, searchQuery, selectedFilters]);

  return { filteredTemplates };
}
